import {Injectable} from '@angular/core';
import {environment} from "@/environments/environment";
import {HttpClient} from "@angular/common/http";
import {Observable} from "rxjs";
import {map} from "rxjs/operators";
import {DashboardData, DashboardResponse, ConsolidatedTransfer, MaintenanceAnalytics} from "@/app/modules/dashboard/core/types";
import { ChecklistHeader, HeaderTypeEnum } from '@/app/modules/checklist-header/core/type';
import { PaginatedResponse } from '@/app/common/utils/pagination/types';

@Injectable({
  providedIn: 'root'
})
export class ApiService {

  private readonly defaultUrl: string = `${environment.apiHost}/dashboard`

  constructor(private http: HttpClient) {
  }

  findAll(): Observable<DashboardResponse<DashboardData>> {
    return this.http.get<DashboardResponse<DashboardData>>(this.defaultUrl).pipe(
      map((response: DashboardResponse<DashboardData>) => ({
        ...response,
        items: response.items.filter((item: DashboardData) => item.user_name !== 'Guilherme Varussa')
      }))
    );
  }

  getTransfersTracking(): Observable<ConsolidatedTransfer[]> {
    const checklistUrl = `${environment.apiHost}/checklistHeaders/transfers`;
    return this.http.get<PaginatedResponse<ChecklistHeader>>(checklistUrl, {
      params: { pageIndex: 1, pageSize: 1000 } 
    }).pipe(
      map(response => {
        const items = response.items;
        const transfers: ConsolidatedTransfer[] = [];
        const chassisMap = new Map<string, ChecklistHeader[]>();

        items.forEach(item => {
          if (!chassisMap.has(item.chassis)) {
            chassisMap.set(item.chassis, []);
          }
          chassisMap.get(item.chassis)!.push(item);
        });

        chassisMap.forEach((checklists) => {
          checklists.sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime());

          const starts = checklists.filter(c => c.type === HeaderTypeEnum.TRANSFER_START);
          
          starts.forEach(start => {
            // Tenta encontrar o recebimento primeiro pelo ID de solicitação, depois pela data
            const done = checklists.find(c => 
              c.type === HeaderTypeEnum.TRANSFER_DONE && 
              (c.header_request_id === start.id || 
               (new Date(c.date).getTime() >= new Date(start.date).getTime() && !c.header_request_id))
            );

            transfers.push({
              chassis: start.chassis,
              productName: start.ProductModel?.name || 'N/A',
              origin: start.origin?.name || 'N/A',
              destination: start.destination?.name || 'N/A',
              sentDate: new Date(start.date),
              receivedDate: done ? new Date(done.date) : undefined,
              sentChecklistId: start.id,
              status: done ? 'received' : 'pending'
            });
          });
        });

        return transfers.sort((a, b) => b.sentDate.getTime() - a.sentDate.getTime());
      })
    );
  }

  getMaintenanceAnalytics(chassis?: string): Observable<MaintenanceAnalytics> {
    const checklistUrl = `${environment.apiHost}/checklistHeaders`;
    return this.http.get<PaginatedResponse<ChecklistHeader>>(checklistUrl, {
      params: { 
        type: HeaderTypeEnum.MAINTENANCE,
        pageIndex: 1,
        pageSize: 1000 
      }
    }).pipe(
      map(response => {
        let items = response.items;
        if (chassis) {
          items = items.filter(i => i.chassis === chassis);
        }

        const monthsPtBr = ['Jan', 'Fev', 'Mar', 'Abr', 'Mai', 'Jun', 'Jul', 'Ago', 'Set', 'Out', 'Nov', 'Dez'];
        const labels: string[] = [];
        const inProgressData: number[] = [];
        const doneData: number[] = [];

        const today = new Date();
        for (let i = 5; i >= 0; i--) {
          const d = new Date(today.getFullYear(), today.getMonth() - i, 1);
          const monthLabel = `${monthsPtBr[d.getMonth()]}/${d.getFullYear()}`;
          labels.push(monthLabel);

          const monthItems = items.filter(item => {
            const itemDate = new Date(item.date);
            return itemDate.getMonth() === d.getMonth() && itemDate.getFullYear() === d.getFullYear();
          });

          inProgressData.push(monthItems.filter(item => item.status === 'in_progress').length);
          doneData.push(monthItems.filter(item => item.status === 'done').length);
        }

        return {
          labels,
          datasets: [
            { label: 'Em andamento', data: inProgressData, backgroundColor: '#f50' },
            { label: 'Finalizadas', data: doneData, backgroundColor: '#87d068' }
          ]
        };
      })
    );
  }
}
